import { useState, useEffect, useMemo, createContext, useContext, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Import all close dialog actions
import { closeDetailsDialog as closeHospitalDialog } from '../store/reducers/hospital.slice';
import { closeDetailsDialog as closeOverallDialog } from '../store/reducers/overall.slice';
import { closeDetailsDialog as closeAdmissionDialog, closeADTDetailsDialog } from '../store/reducers/admission.slice';
import { closeDetailsDialog as closeDeceasedDialog } from '../store/reducers/deceased.slice';
import { closeDetailsDialog as closeCommunityTransferDialog } from '../store/reducers/community-transfer.slice';

// Global orientation context
const OrientationContext = createContext();

// Global orientation state - only one instance
let globalOrientationState = {
    isPortrait: false,
    showOrientationAlert: false,
    pendingAction: null,
    prevIsPortrait: false,
    hasShownAlert: false,
    listeners: new Set(),
    isDialogOpen: false
};

let globalCheckOrientation = null;
let globalSetShowOrientationAlert = null;

// Provider component
export const OrientationProvider = ({ children }) => {
    const [isPortrait, setIsPortrait] = useState(false);
    const [showOrientationAlert, setShowOrientationAlert] = useState(false);
    const setShowOrientationAlertRef = useRef(null);
    const dispatch = useDispatch();

    // Get dialog state from Redux stores
    const hospitalDialog = useSelector(state => state.hospital?.detailsDialog?.isOpen);
    const overallDialog = useSelector(state => state.overall?.detailsDialog?.isOpen);
    const admissionDialog = useSelector(state => state.admission?.detailsDialog?.isOpen);
    const admissionADTDialog = useSelector(state => state.admission?.detailsADTDialog?.isOpen);
    const deceasedDialog = useSelector(state => state.deceased?.detailsDialog?.isOpen);
    const communityTransferDialog = useSelector(state => state.communityTransfer?.detailsDialog?.isOpen);
    
    const isDialogOpen = useMemo(() => {
        return hospitalDialog || overallDialog || admissionDialog || admissionADTDialog || deceasedDialog || communityTransferDialog;
    }, [hospitalDialog, overallDialog, admissionDialog, admissionADTDialog, deceasedDialog, communityTransferDialog]);

    useEffect(() => {
        globalOrientationState.isDialogOpen = isDialogOpen;
    }, [isDialogOpen]);

    useEffect(() => {
        // Store the setShowOrientationAlert function globally
        setShowOrientationAlertRef.current = setShowOrientationAlert;
        globalSetShowOrientationAlert = setShowOrientationAlert;
        
        if (!globalCheckOrientation) {
            globalCheckOrientation = () => {
                const isPortraitMode = window.innerHeight > window.innerWidth;
                const wasPortrait = globalOrientationState.prevIsPortrait;
                const currentPendingAction = globalOrientationState.pendingAction;
                const currentIsDialogOpen = globalOrientationState.isDialogOpen;

                globalOrientationState.isPortrait = isPortraitMode;
                setIsPortrait(isPortraitMode);

                // If orientation changed from portrait to landscape and we have a pending action, execute it
                if (wasPortrait && !isPortraitMode && currentPendingAction) {
                    const { action, cardTitle, handleOnClickAction } = currentPendingAction;
                    if (handleOnClickAction) {
                        handleOnClickAction(cardTitle, action);
                    }
                    globalOrientationState.pendingAction = null;
                    globalOrientationState.showOrientationAlert = false;
                    if (globalSetShowOrientationAlert) {
                        globalSetShowOrientationAlert(false);
                    }
                    globalOrientationState.hasShownAlert = false;
                }
                
                // If orientation changed from landscape to portrait and dialog is open, show rotate alert (only once)
                if (!wasPortrait && isPortraitMode && currentIsDialogOpen && !globalOrientationState.hasShownAlert) {
                    globalOrientationState.showOrientationAlert = true;
                    if (globalSetShowOrientationAlert) {
                        globalSetShowOrientationAlert(true);
                    }
                }
                
                // If orientation changed from portrait to landscape, hide rotate alert and reset flag for next time
                if (wasPortrait && !isPortraitMode) {
                    globalOrientationState.showOrientationAlert = false;
                    if (globalSetShowOrientationAlert) {
                        globalSetShowOrientationAlert(false);
                    }
                    // Reset the flag so the alert can show again next time user rotates back to portrait
                    globalOrientationState.hasShownAlert = false;
                }
                
                // Reset the alert flag when rotating from portrait to landscape (allow alert to show again next time)
                if (wasPortrait && !isPortraitMode) {
                    globalOrientationState.hasShownAlert = false;
                }

                // If dialog is closed, reset the alert flag
                if (!currentIsDialogOpen) {
                    globalOrientationState.hasShownAlert = false;
                }

                globalOrientationState.prevIsPortrait = isPortraitMode;
            };

            // Initial check
            globalCheckOrientation();

            // Add event listener
            window.addEventListener('resize', globalCheckOrientation);
        }

        return () => {
            // Don't remove listener here - let it persist globally
        };
    }, [setShowOrientationAlert]);

    const handleOrientationAlertClose = () => {
        globalOrientationState.showOrientationAlert = false;
        if (globalSetShowOrientationAlert) {
            globalSetShowOrientationAlert(false);
        }
        globalOrientationState.hasShownAlert = false;
        
        // Close any open mobile menus and parent dialogs when rotate dialog is manually closed in portrait mode
        if (globalOrientationState.isPortrait) {
            // Close all open dialogs since they shouldn't be open in portrait mode
            dispatch(closeHospitalDialog());
            dispatch(closeOverallDialog());
            dispatch(closeAdmissionDialog());
            dispatch(closeADTDetailsDialog());
            dispatch(closeDeceasedDialog());
            dispatch(closeCommunityTransferDialog());
            
            // Dispatch a custom event to notify components to close their mobile menus
            window.dispatchEvent(new CustomEvent('closeMobileMenus'));
        }
    };

    const clearPendingAction = () => {
        globalOrientationState.pendingAction = null;
    };

    const checkOrientationBeforeAction = (action, cardTitle, handleOnClickAction, isMobile) => {
        if (isMobile && globalOrientationState.isPortrait) {
            globalOrientationState.pendingAction = { action, cardTitle, handleOnClickAction };
            globalOrientationState.showOrientationAlert = true;
            setShowOrientationAlert(true);
            return false;
        }
        return true;
    };

    return (
        <OrientationContext.Provider value={{
            isPortrait,
            showOrientationAlert,
            handleOrientationAlertClose,
            checkOrientationBeforeAction,
            clearPendingAction
        }}>
            {children}
        </OrientationContext.Provider>
    );
};

// Hook to use orientation context
const useOrientation = (isMobile = false) => {
    const context = useContext(OrientationContext);
    if (!context) {
        throw new Error('useOrientation must be used within an OrientationProvider');
    }

    const { checkOrientationBeforeAction: contextCheckOrientation, ...rest } = context;

    const checkOrientationBeforeAction = (action, cardTitle, handleOnClickAction) => {
        return contextCheckOrientation(action, cardTitle, handleOnClickAction, isMobile);
    };

    return {
        ...rest,
        checkOrientationBeforeAction
    };
};

export default useOrientation; 